// Logout automático depois de LIMITE_MIN minutos sem uso.
//
// - Qualquer aba do Quality aberta neste navegador conta como uso (o último
//   movimento fica em localStorage e é compartilhado entre as abas).
// - Telas de painel (TVs/periféricos) não desconectam. Como o login é o mesmo em
//   todas as abas, enquanto houver um painel aberto neste navegador nenhuma aba
//   desconecta (o painel avisa que está aberto a cada 30 s).
// - Um minuto antes, aparece o aviso com o botão "Continuar conectado".
import { useCallback, useEffect, useRef, useState } from 'react';

export const LIMITE_MIN = 30;
const AVISO_SEG = 60;
const CHAVE_ATIVIDADE = 'quality_ultima_atividade';
const CHAVE_PAINEL = 'quality_painel_aberto_em';

// Telas que ficam abertas o dia inteiro em TVs/periféricos.
export function ehTelaDePainel(location) {
  const p = location.pathname;
  const fullscreen = new URLSearchParams(location.search).get('fullscreen') === '1';
  return p === '/painel-captacao'
    || (p === '/gestao-captacao' && fullscreen)
    || p.startsWith('/gestao-ocorrencias')
    || p.startsWith('/intercorrencias')
    || p === '/gestao-contas-enviadas'
    || p === '/pendencia-orcamentos';
}

function ler(chave) {
  try { return Number(localStorage.getItem(chave)) || 0; } catch { return 0; }
}
function gravar(chave, valor) {
  try { localStorage.setItem(chave, String(valor)); } catch { /* modo privado: segue só nesta aba */ }
}

export function useLogoutPorInatividade({ ativo, emPainel, aoExpirar }) {
  const [segundosRestantes, setSegundosRestantes] = useState(null);
  const ultimaLocal = useRef(Date.now());

  const registrarAtividade = useCallback(() => {
    const agora = Date.now();
    // grava no máximo a cada 5 s para não sobrecarregar o navegador
    if (agora - ultimaLocal.current < 5000) return;
    ultimaLocal.current = agora;
    gravar(CHAVE_ATIVIDADE, agora);
  }, []);

  const continuar = useCallback(() => {
    ultimaLocal.current = 0;
    registrarAtividade();
    setSegundosRestantes(null);
  }, [registrarAtividade]);

  useEffect(() => {
    if (!ativo) return undefined;
    gravar(CHAVE_ATIVIDADE, Date.now());
    const eventos = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'wheel'];
    eventos.forEach(e => window.addEventListener(e, registrarAtividade, { passive: true }));
    return () => eventos.forEach(e => window.removeEventListener(e, registrarAtividade));
  }, [ativo, registrarAtividade]);

  useEffect(() => {
    if (!ativo) return undefined;
    const verificar = () => {
      const agora = Date.now();
      if (emPainel) { gravar(CHAVE_PAINEL, agora); setSegundosRestantes(null); return; }
      if (agora - ler(CHAVE_PAINEL) < 2 * 60 * 1000) { setSegundosRestantes(null); return; }
      const parado = (agora - Math.max(ler(CHAVE_ATIVIDADE), ultimaLocal.current)) / 1000;
      const restante = Math.ceil(LIMITE_MIN * 60 - parado);
      if (restante <= 0) { setSegundosRestantes(null); aoExpirar(); return; }
      setSegundosRestantes(restante <= AVISO_SEG ? restante : null);
    };
    verificar();
    const id = setInterval(verificar, 1000);
    return () => clearInterval(id);
  }, [ativo, emPainel, aoExpirar]);

  return { segundosRestantes, continuar };
}
